import { Receipt } from "lucide-react";
import { describePaymentPlan, formatPesos } from "@/lib/paymentPlanBreakdown";

// =====================================================================
// PaymentPlanBreakdown — "What the patient pays", in real pesos.
// =====================================================================
// A read-only summary that turns the selected billing model into the
// concrete peso lines a patient/staff member reads. Shared by:
//   * SetBillingModelDialog — live preview as the model is chosen.
//   * BillingStateCard      — the saved plan's breakdown.
// Both feed the SAME describePaymentPlan() helper, so the preview and
// the saved view can never disagree.
//
// Purely presentational — it computes nothing the database doesn't also
// compute; it only formats. Renders null when there isn't enough to
// show (no preset yet, or a zero total).
// =====================================================================

const TONE = {
  normal: "text-slate-800",
  muted: "text-slate-400",
  accent: "text-teal-800",
};

export default function PaymentPlanBreakdown({
  preset,
  totalCentavos,
  visitCount,
  dpCentavos,
  minPerVisitCentavos,
  numPayments,
  className = "",
  "data-testid": testId = "payment-plan-breakdown",
}) {
  const { ready, lines, footnote } = describePaymentPlan({
    preset,
    totalCentavos,
    visitCount,
    dpCentavos,
    minPerVisitCentavos,
    numPayments,
  });

  if (!ready || lines.length === 0) return null;

  return (
    <div
      className={[
        "rounded-lg border border-teal-200 bg-teal-50/60 px-3 py-2.5",
        className,
      ].join(" ")}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <Receipt className="w-3.5 h-3.5 text-teal-700" strokeWidth={2.25} aria-hidden="true" />
        <span className="text-[10px] uppercase tracking-wider font-semibold text-teal-800">
          What the patient pays
        </span>
      </div>

      <dl className="space-y-0.5">
        {lines.map((l) => (
          <div key={l.key} className="flex items-baseline justify-between gap-3 text-[12px]">
            <dt className={l.tone === "accent" ? "font-semibold text-teal-900" : "text-slate-600"}>
              {l.label}
            </dt>
            <dd className={`tabular-nums shrink-0 ${l.tone === "accent" ? "font-semibold" : "font-medium"} ${TONE[l.tone] || TONE.normal}`}>
              {l.value != null ? formatPesos(l.value) : l.text || "—"}
            </dd>
          </div>
        ))}
      </dl>

      {footnote ? (
        <p className="text-[11px] text-teal-800/80 mt-1.5 leading-snug">{footnote}</p>
      ) : null}
    </div>
  );
}
